import React, { useState } from "react";

import SecondarySignals from "./SecondarySignals";
import {
  cardCta,
  resolveSignalCopy,
  resolveTemplate,
  templateFields,
} from "./signalCTAs";
import SignalGuideModal from "./SignalGuideModal";
import { useSignalCtaRouter } from "./useSignalCtaRouter";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  recordSignalCtaClick,
  toggleGuideStep,
} from "../../Store/Reducers/SignalFeedSlice";
import type {
  SignalCard,
  SignalTier,
} from "../../Store/Reducers/SignalFeedSlice";

/** Pixel-matched to the mock's `.pm-compact-card` (used in the Portfolio Map
 * right panel's compact rail) — CSS lines 1356-1400, 1454-1463; markup from
 * `renderCompactCard(sig, a)`, lines 6865-6889. Note the mock deliberately
 * shows the raw `sig.key` (e.g. "renewal-no-foundation"), not a human title
 * — matched literally, this is a denser/more technical context than the
 * full `.feed-card`. */
const TIER_COMPACT: Record<
  SignalTier,
  { icon: string; label: string; bg: string; color: string }
> = {
  critical: { icon: "⬛", label: "CRITICAL", bg: "#fef0f0", color: "#c0392b" },
  warning: { icon: "⚠", label: "WARNING", bg: "#fff8ee", color: "#b7591a" },
  opp: { icon: "↑", label: "OPPORTUNITY", bg: "#f0faf5", color: "#1a7a50" },
  maint: { icon: "○", label: "MAINTENANCE", bg: "#f6f8fa", color: "#555" },
};

interface CompactActionCardProps {
  card: SignalCard;
  busy: boolean;
  onSnooze: (firingId: string) => void;
  onDismiss: (firingId: string) => void;
  withOrg: (path: string) => string;
  onOpenAssess: (account: { id: string; name: string }) => void;
  /** mock's "View in Actions →" switches the page to the Actions tab and
   * flashes this specific card (`openActionsFromMap`/`flashActionsCard`) —
   * optional here since wiring it requires a tab-change callback from the
   * page shell; omitted, button hidden, if not supplied by the caller. */
  onViewInActions?: (signalKey: string) => void;
}

const CompactActionCard: React.FC<CompactActionCardProps> = ({
  card,
  onOpenAssess,
  withOrg,
  onViewInActions,
}) => {
  const tierMeta = TIER_COMPACT[card.tier] ?? TIER_COMPACT.maint;
  const parameters = useAppSelector(state => state.signalFeed.parameters);
  const fields = templateFields(card.evidence?.triggeredFields, parameters);
  const copy = resolveSignalCopy(card.signal_key, fields, card.cta?.card_copy);
  const { label: rawCtaLabel, kind: ctaKind, path: ctaPath } = cardCta(card);
  // An approved plan's button is its first step's title, which may hold
  // placeholders ({{al}}, {{al-stale-days}}) like the rest of the card.
  const ctaLabel = resolveTemplate(rawCtaLabel, fields);

  const dispatch = useAppDispatch();
  const routeCta = useSignalCtaRouter(withOrg, onOpenAssess);
  const guide = card.cta?.guide ?? null;
  const [guideOpen, setGuideOpen] = useState(false);

  const handleCTA = () => {
    void dispatch(
      recordSignalCtaClick({ firingId: card.signal_firing_id, label: ctaLabel })
    );
    if (guide) {
      setGuideOpen(true);
      return;
    }
    routeCta(card, ctaKind, ctaPath);
  };

  return (
    <div className="rounded-[9px] border border-[#e5e7eb] bg-white px-[10px] py-[9px]">
      <div className="mb-[6px] flex flex-wrap items-center gap-[7px]">
        <span
          className="inline-flex items-center gap-1 rounded-[6px] px-[7px] py-[3px] text-[10px] font-extrabold tracking-[0.05em]"
          style={{ color: tierMeta.color, background: tierMeta.bg }}
        >
          {tierMeta.icon} {tierMeta.label}
        </span>
        <span className="text-[11px] font-bold text-[#0f1623]">
          {copy.title}
        </span>
      </div>
      <div className="mb-[7px] text-xs leading-[1.42] text-[#6b7280]">
        {copy.text}
      </div>
      <SecondarySignals card={card} compact />
      {card.cta?.awareness_note && (
        <div className="mb-[7px] text-[11px] leading-[1.4] text-sky-800">
          {card.cta.awareness_note}
        </div>
      )}
      {card.cta?.detail && (
        <p className="mb-[7px] m-0 text-[11px] leading-[1.4] text-[#4b5563]">
          {resolveTemplate(card.cta.detail, fields)}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-[10px]">
        <button
          type="button"
          onClick={handleCTA}
          className="rounded-[5px] px-[10px] py-1 text-[11.5px] font-semibold"
          style={{
            color: tierMeta.color,
            background: tierMeta.bg,
            border: `1px solid ${tierMeta.color}`,
          }}
        >
          {ctaLabel}
        </button>
        {onViewInActions && (
          <button
            type="button"
            onClick={() => onViewInActions(card.signal_key)}
            className="bg-none p-0 text-[11.5px] font-semibold text-brand hover:underline"
          >
            View in Actions →
          </button>
        )}
      </div>
      {guide && (
        <SignalGuideModal
          isOpen={guideOpen}
          onClose={() => setGuideOpen(false)}
          guide={guide}
          accountName={card.account_name}
          signalTitle={copy.title}
          tier={card.tier}
          fields={fields}
          planBucket={card.cta?.plan?.bucket ?? null}
          primaryLabel={card.cta?.plan ? undefined : ctaLabel}
          onPrimary={() => {
            setGuideOpen(false);
            routeCta(card, ctaKind, ctaPath);
          }}
          onStepLink={link => {
            setGuideOpen(false);
            routeCta(card, link.kind, link.path);
          }}
          progress={card.guide_progress ?? []}
          onToggleStep={(step, title, done) =>
            void dispatch(
              toggleGuideStep({
                firingId: card.signal_firing_id,
                step,
                title,
                done,
              })
            )
          }
        />
      )}
    </div>
  );
};

export default CompactActionCard;
